import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleDollarSign, Loader2, Percent, Crown, Plus, Trash2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { API_PATH, request } from '../config/api';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import { useAuth } from '../store/AuthContext';
import { useConfirm } from '../store/ConfirmDialogContext';
import styles from './PollChargePage.module.css';

const PollChargePage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const isSuperadmin = user?.role === 'superadmin';

    // ── Coins per poll ──────────────────────────────────────────────
    const [coinCost, setCoinCost] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    // ── Coin purchase bulk bonus ────────────────────────────────────
    const [bonusThreshold, setBonusThreshold] = useState('');
    const [bonusPercent, setBonusPercent] = useState('');
    const [bonusLoading, setBonusLoading] = useState(true);
    const [bonusSaving, setBonusSaving] = useState(false);
    const [bonusMessage, setBonusMessage] = useState({ type: '', text: '' });

    // ── Platinum subscription tiers ─────────────────────────────────
    const [tiers, setTiers] = useState([]);
    const [tiersLoading, setTiersLoading] = useState(true);
    const [tiersSaving, setTiersSaving] = useState(false);
    const [tiersMessage, setTiersMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        if (!isSuperadmin) {
            navigate('/dashboard', { replace: true });
            return;
        }

        const fetchCoinCost = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await request(`${API_PATH}/superadmin/settings/coin-cost-per-poll`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    setError(data.error || 'Failed to load');
                    return;
                }
                setCoinCost(String(data.coinCostPerPoll ?? 10));
            } catch (e) {
                setError('Network error');
            } finally {
                setLoading(false);
            }
        };

        const fetchBonus = async () => {
            setBonusLoading(true);
            try {
                const res = await request(`${API_PATH}/superadmin/settings/coin-bonus`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    setBonusMessage({ type: 'error', text: data.error || 'Failed to load' });
                    return;
                }
                setBonusThreshold(String(data.threshold ?? 100));
                setBonusPercent(String(Math.round(((data.rate ?? 1.5) - 1) * 100)));
            } catch (e) {
                setBonusMessage({ type: 'error', text: 'Network error' });
            } finally {
                setBonusLoading(false);
            }
        };

        const fetchTiers = async () => {
            setTiersLoading(true);
            try {
                const res = await request(`${API_PATH}/superadmin/settings/platinum-tiers`);
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    setTiersMessage({ type: 'error', text: data.error || 'Failed to load' });
                    return;
                }
                setTiers((data.tiers || []).map((t) => ({ months: String(t.months), priceRupees: String(t.priceRupees) })));
            } catch (e) {
                setTiersMessage({ type: 'error', text: 'Network error' });
            } finally {
                setTiersLoading(false);
            }
        };

        fetchCoinCost();
        fetchBonus();
        fetchTiers();
    }, [isSuperadmin, navigate]);

    const handleSave = async (e) => {
        e.preventDefault();
        const num = parseInt(coinCost, 10);
        if (!Number.isInteger(num) || num < 0) {
            setMessage({ type: 'error', text: 'Enter a valid non-negative number (e.g. 10)' });
            return;
        }
        const shouldSave = await confirm({
            title: 'Save poll charge?',
            message: `Set the charge to ${num} coin${num === 1 ? '' : 's'} per poll?`,
            confirmText: 'Save',
        });
        if (!shouldSave) return;
        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await request(`${API_PATH}/superadmin/settings/coin-cost-per-poll`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ coinCostPerPoll: num }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setMessage({ type: 'error', text: data.error || 'Failed to update' });
                return;
            }
            setCoinCost(String(data.coinCostPerPoll));
            setMessage({ type: 'success', text: 'Per poll charge updated.' });
        } catch (e) {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSaveBonus = async (e) => {
        e.preventDefault();
        const threshold = Number(bonusThreshold);
        const percent = Number(bonusPercent);
        if (!Number.isFinite(threshold) || threshold < 0) {
            setBonusMessage({ type: 'error', text: 'Enter a valid non-negative minimum amount' });
            return;
        }
        if (!Number.isFinite(percent) || percent < 0) {
            setBonusMessage({ type: 'error', text: 'Enter a valid non-negative bonus percentage' });
            return;
        }
        const shouldSave = await confirm({
            title: 'Save coin purchase bonus?',
            message: `Apply a ${percent}% bonus for purchases of ₹${threshold} or more?`,
            confirmText: 'Save',
        });
        if (!shouldSave) return;
        setBonusSaving(true);
        setBonusMessage({ type: '', text: '' });
        try {
            const res = await request(`${API_PATH}/superadmin/settings/coin-bonus`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ threshold, rate: 1 + percent / 100 }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setBonusMessage({ type: 'error', text: data.error || 'Failed to update' });
                return;
            }
            setBonusThreshold(String(data.threshold));
            setBonusPercent(String(Math.round((data.rate - 1) * 100)));
            setBonusMessage({ type: 'success', text: 'Coin purchase bonus updated.' });
        } catch (e) {
            setBonusMessage({ type: 'error', text: 'Network error' });
        } finally {
            setBonusSaving(false);
        }
    };

    const handleTierChange = (index, field, value) => {
        setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
    };

    const handleAddTier = () => {
        setTiers((prev) => [...prev, { months: '', priceRupees: '' }]);
    };

    const handleRemoveTier = async (index) => {
        const tier = tiers[index];
        const shouldRemove = await confirm({
            title: 'Remove Platinum tier?',
            message: `Remove the ${tier?.months || 'selected'}-month subscription tier? Save the tiers afterward to apply this change.`,
            confirmText: 'Remove',
            tone: 'danger',
        });
        if (!shouldRemove) return;
        setTiers((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSaveTiers = async (e) => {
        e.preventDefault();
        if (tiers.length === 0) {
            setTiersMessage({ type: 'error', text: 'At least one tier is required' });
            return;
        }
        const parsed = [];
        const seenMonths = new Set();
        for (const t of tiers) {
            const months = parseInt(t.months, 10);
            const priceRupees = Number(t.priceRupees);
            if (!Number.isInteger(months) || months <= 0) {
                setTiersMessage({ type: 'error', text: 'Every tier needs a whole number of months greater than 0' });
                return;
            }
            if (!Number.isFinite(priceRupees) || priceRupees <= 0) {
                setTiersMessage({ type: 'error', text: 'Every tier needs a price greater than ₹0' });
                return;
            }
            if (seenMonths.has(months)) {
                setTiersMessage({ type: 'error', text: `Duration ${months} month(s) is listed more than once` });
                return;
            }
            seenMonths.add(months);
            parsed.push({ months, priceRupees });
        }
        const shouldSave = await confirm({
            title: 'Save Platinum tiers?',
            message: `Save ${parsed.length} subscription tier${parsed.length === 1 ? '' : 's'}?`,
            confirmText: 'Save',
        });
        if (!shouldSave) return;
        setTiersSaving(true);
        setTiersMessage({ type: '', text: '' });
        try {
            const res = await request(`${API_PATH}/superadmin/settings/platinum-tiers`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tiers: parsed }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setTiersMessage({ type: 'error', text: data.error || 'Failed to update' });
                return;
            }
            setTiers((data.tiers || []).map((t) => ({ months: String(t.months), priceRupees: String(t.priceRupees) })));
            setTiersMessage({ type: 'success', text: 'Platinum tiers updated.' });
        } catch (e) {
            setTiersMessage({ type: 'error', text: 'Network error' });
        } finally {
            setTiersSaving(false);
        }
    };

    if (!isSuperadmin) return null;

    const monthlyBaseRate = (() => {
        const oneMonth = tiers.find((t) => Number(t.months) === 1);
        if (oneMonth) return Number(oneMonth.priceRupees) || 0;
        const cheapest = tiers.reduce((min, t) => {
            const perMonth = Number(t.priceRupees) / (Number(t.months) || 1);
            return Number.isFinite(perMonth) && (min == null || perMonth < min) ? perMonth : min;
        }, null);
        return cheapest || 0;
    })();

    const preventNegativeNumberKey = (event) => {
        if (['-', '+', 'e', 'E'].includes(event.key)) event.preventDefault();
    };

    const updateNonNegativeValue = (setter) => (event) => {
        const value = event.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) setter(value);
    };

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={CircleDollarSign}
                title="Pricing"
                subtitle="Manage what admins pay across LivePulse — poll charges, coin purchase bonuses, and Platinum subscription tiers. Changes apply immediately to new purchases."
            />

            {error && (
                <div className={styles.errorBanner} role="alert">
                    {error}
                </div>
            )}

            <div className={styles.settingsGrid}>
            <section className={styles.card}>
                <h2 className={styles.sectionTitle}>Per poll charge</h2>
                {loading ? (
                    <div className={styles.loading}>
                        <Loader2 size={28} className={styles.spinner} />
                        Loading…
                    </div>
                ) : (
                    <form onSubmit={handleSave} className={styles.form}>
                        <div className={styles.field}>
                            <label htmlFor="coin-cost" className={styles.label}>Coins per poll</label>
                            <Input
                                id="coin-cost"
                                type="number"
                                min={0}
                                value={coinCost}
                                onChange={updateNonNegativeValue(setCoinCost)}
                                onKeyDown={preventNegativeNumberKey}
                                placeholder="10"
                                disabled={saving}
                            />
                            <p className={styles.hint}>This many coins will be deducted from an admin when they create a poll (if they do not have a Platinum subscription).</p>
                        </div>
                        {message.text && (
                            <p className={message.type === 'success' ? styles.messageSuccess : styles.messageError} role="alert">
                                {message.text}
                            </p>
                        )}
                        <Button type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 size={16} className={styles.spin} /> : null} className={styles.pillCta}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </form>
                )}
            </section>

            <section className={styles.card}>
                <h2 className={styles.sectionTitle}>
                    <Percent size={18} className={styles.sectionTitleIcon} />
                    Coin purchase bonus
                </h2>
                {bonusLoading ? (
                    <div className={styles.loading}>
                        <Loader2 size={28} className={styles.spinner} />
                        Loading…
                    </div>
                ) : (
                    <form onSubmit={handleSaveBonus} className={styles.form}>
                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label htmlFor="bonus-threshold" className={styles.label}>Minimum amount (₹)</label>
                                <Input
                                    id="bonus-threshold"
                                    type="number"
                                    min={0}
                                    value={bonusThreshold}
                                    onChange={updateNonNegativeValue(setBonusThreshold)}
                                    onKeyDown={preventNegativeNumberKey}
                                    placeholder="100"
                                    disabled={bonusSaving}
                                />
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="bonus-percent" className={styles.label}>Bonus (%)</label>
                                <Input
                                    id="bonus-percent"
                                    type="number"
                                    min={0}
                                    value={bonusPercent}
                                    onChange={updateNonNegativeValue(setBonusPercent)}
                                    onKeyDown={preventNegativeNumberKey}
                                    placeholder="50"
                                    disabled={bonusSaving}
                                />
                            </div>
                        </div>
                        <p className={styles.hint}>
                            When an admin buys coins worth ₹{bonusThreshold || '0'} or more, they receive {bonusPercent || '0'}% extra coins.
                        </p>
                        {bonusMessage.text && (
                            <p className={bonusMessage.type === 'success' ? styles.messageSuccess : styles.messageError} role="alert">
                                {bonusMessage.text}
                            </p>
                        )}
                        <Button type="submit" variant="primary" disabled={bonusSaving} icon={bonusSaving ? <Loader2 size={16} className={styles.spin} /> : null} className={styles.pillCta}>
                            {bonusSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </form>
                )}
            </section>
            </div>

            <section className={styles.card}>
                <h2 className={styles.sectionTitle}>
                    <Crown size={18} className={styles.sectionTitleIcon} />
                    Platinum subscription tiers
                </h2>
                {tiersLoading ? (
                    <div className={styles.loading}>
                        <Loader2 size={28} className={styles.spinner} />
                        Loading…
                    </div>
                ) : (
                    <form onSubmit={handleSaveTiers} className={styles.form}>
                        <div className={styles.tierList}>
                            {tiers.map((tier, index) => {
                                const months = Number(tier.months);
                                const price = Number(tier.priceRupees);
                                const discountPercent = monthlyBaseRate > 0 && months > 0 && Number.isFinite(price)
                                    ? Math.round((1 - price / (monthlyBaseRate * months)) * 100)
                                    : null;
                                return (
                                    <div key={index} className={styles.tierRow}>
                                        <div className={styles.field}>
                                            <label htmlFor={`tier-months-${index}`} className={styles.label}>Months</label>
                                            <Input
                                                id={`tier-months-${index}`}
                                                type="number"
                                                min={1}
                                                value={tier.months}
                                                onChange={(e) => {
                                                    if (e.target.value === '' || /^\d*$/.test(e.target.value)) {
                                                        handleTierChange(index, 'months', e.target.value);
                                                    }
                                                }}
                                                onKeyDown={preventNegativeNumberKey}
                                                placeholder="1"
                                                disabled={tiersSaving}
                                            />
                                        </div>
                                        <div className={styles.field}>
                                            <label htmlFor={`tier-price-${index}`} className={styles.label}>Price (₹)</label>
                                            <Input
                                                id={`tier-price-${index}`}
                                                type="number"
                                                min={1}
                                                value={tier.priceRupees}
                                                onChange={updateNonNegativeValue((value) => handleTierChange(index, 'priceRupees', value))}
                                                onKeyDown={preventNegativeNumberKey}
                                                placeholder="2000"
                                                disabled={tiersSaving}
                                            />
                                        </div>
                                        <span className={styles.tierDiscountHint}>
                                            {discountPercent != null && discountPercent !== 0 ? `${discountPercent > 0 ? '−' : '+'}${Math.abs(discountPercent)}%` : ''}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.tierRemoveBtn}
                                            onClick={() => handleRemoveTier(index)}
                                            disabled={tiersSaving}
                                            aria-label="Remove tier"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        <Button type="button" variant="secondary" size="sm" onClick={handleAddTier} disabled={tiersSaving} icon={<Plus size={16} />}>
                            Add tier
                        </Button>
                        <p className={styles.hint}>
                            Discount % shown per row is calculated against the 1‑month price, for reference only.
                        </p>
                        {tiersMessage.text && (
                            <p className={tiersMessage.type === 'success' ? styles.messageSuccess : styles.messageError} role="alert">
                                {tiersMessage.text}
                            </p>
                        )}
                        <Button type="submit" variant="primary" disabled={tiersSaving} icon={tiersSaving ? <Loader2 size={16} className={styles.spin} /> : null} className={styles.pillCta}>
                            {tiersSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </form>
                )}
            </section>
        </div>
    );
};

export default PollChargePage;
